import { useCallback, useEffect, useRef } from 'react';
import { btlFailureNotice } from '../components/video-gen/btlFailureNotice';
import type { CustomToast } from '../context/NotificationContext';
import { ToastTypes } from '../types/notificationTypes';

/**
 * SYM-6337. Announces a failed Bring to Life clip when its window is closed.
 *
 * Returns a function that never changes, and that reads whether the window is
 * open at the moment it is CALLED. That is the whole point: useVideoPolling
 * keeps the handlers it started with (its effect only restarts on a new
 * generation id), so a handler that read `windowOpen` directly would see the
 * value from when the clip was requested -- open -- and stay silent after
 * "Close and wait", which is exactly the failure being fixed.
 */
export const useBtlFailureAnnouncer = (
  windowOpen: boolean,
  addToast: (toast: CustomToast) => void
): ((reason: string | null | undefined) => void) => {
  const openRef = useRef(windowOpen);
  const toastRef = useRef(addToast);

  useEffect(() => {
    openRef.current = windowOpen;
  }, [windowOpen]);
  useEffect(() => {
    toastRef.current = addToast;
  }, [addToast]);

  return useCallback((reason) => {
    const notice = btlFailureNotice(openRef.current, reason);
    if (notice) toastRef.current({ type: ToastTypes.ERROR, ...notice });
  }, []);
};
